import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Chip, 
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Box,
  Divider,
  Grid
} from '@mui/material';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { green, orange, red, blue, grey } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import MailIcon from '@mui/icons-material/Mail';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import { useAuth } from '../../hooks/useAuth';
import { enviarMensagemPersonalizada } from '../../services/preInscricaoService';
import GerenciadorStatus from './GerenciadorStatus';
import StatusHistorico from './StatusHistorico';

const PreInscricaoCard = ({ preInscricao }) => {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historicoDialogOpen, setHistoricoDialogOpen] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [assunto, setAssunto] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const { id, estudante, campanha, status, createdAt } = preInscricao;
  const formattedDate = new Date(createdAt).toLocaleDateString();

  const statusConfig = {
    Pendente: { 
      color: orange[500], 
      icon: <HourglassEmptyIcon />,
      backgroundColor: orange[500]
    },
    Em_Analise: {
      color: blue[500],
      icon: <SearchIcon />,
      backgroundColor: blue[500]
    },
    Aprovada: { 
      color: green[500], 
      icon: <CheckCircleIcon />,
      backgroundColor: green[500]
    },
    Matriculado: {
      color: blue[700],
      icon: <SchoolIcon />,
      backgroundColor: blue[700]
    },
    Rejeitada: { 
      color: red[500], 
      icon: <CancelIcon />,
      backgroundColor: red[500]
    },
    Desistente: {
      color: grey[500],
      icon: <BlockIcon />,
      backgroundColor: grey[500]
    }
  };

  const handleEnviarMensagem = async () => {
    setLoading(true);
    try {
      console.log('Enviando mensagem...', { assunto, mensagem });
      const response = await enviarMensagemPersonalizada(id, {
        assunto,
        mensagem
      });
      
      console.log('Resposta do envio:', response);
      setDialogOpen(false);
      setMensagem('');
      setAssunto('');
      setSnackbar({
        open: true,
        message: 'Mensagem enviada com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro detalhado:', error);
      let errorMessage = 'Erro ao enviar mensagem. Tente novamente.';
      
      if (error.response?.data?.mensagem) {
        errorMessage = error.response.data.mensagem;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tempo limite excedido. Tente novamente.';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStatusChange = (novoStatus) => {
    preInscricao.status = novoStatus;
    setSnackbar({
      open: true,
      message: 'Status atualizado com sucesso!',
      severity: 'success'
    });
  };

  const VerStatusHistorico = () => (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<HistoryIcon />}
        onClick={() => setHistoricoDialogOpen(true)}
      >
        Ver Status
      </Button>

      <Dialog
        open={historicoDialogOpen}
        onClose={() => setHistoricoDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Histórico de Status</DialogTitle>
        <DialogContent>
          <StatusHistorico preInscricaoId={preInscricao.id} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <Card sx={{ 
      position: 'relative',
      '&:hover': {
        boxShadow: 6,
        transition: 'box-shadow 0.3s ease-in-out'
      }
    }}>
      <CardContent>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="h6" component="div">
            Pré-Inscrição #{id}
          </Typography>
          <Chip
            label={status}
            icon={statusConfig[status]?.icon}
            sx={{
              backgroundColor: statusConfig[status]?.backgroundColor,
              color: 'white'
            }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Estudante:</strong>
            </Typography>
            <Typography variant="body1" gutterBottom>
              {estudante.nome}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Email:</strong>
            </Typography>
            <Typography variant="body1">
              {estudante.email}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Campanha:</strong>
            </Typography>
            <Typography variant="body1" gutterBottom>
              {campanha.nome}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Data:</strong>
            </Typography>
            <Typography variant="body1">
              {formattedDate}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <Stack 
          direction="row" 
          spacing={1}
          sx={{ 
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<VisibilityIcon />}
            component={Link}
            to={`/pre-inscricoes/${id}`}
          >
            Detalhes
          </Button>

          {user.tipo === 'instituicao' ? (
            <>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<MailIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Mensagem
              </Button>
              <GerenciadorStatus
                preInscricaoId={id}
                statusAtual={status}
                onStatusChange={handleStatusChange}
              />
            </>
          ) : (
            <VerStatusHistorico />
          )}
        </Stack>
      </CardContent>

      {/* Diálogo para enviar mensagem */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Enviar Mensagem Personalizada</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Assunto"
            type="text"
            fullWidth
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Mensagem"
            multiline
            rows={4}
            fullWidth
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleEnviarMensagem} 
            variant="contained" 
            color="primary"
            disabled={loading || !mensagem.trim() || !assunto.trim()}
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

PreInscricaoCard.propTypes = {
  preInscricao: PropTypes.shape({
    id: PropTypes.number.isRequired,
    estudante: PropTypes.shape({
      nome: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }).isRequired,
    campanha: PropTypes.shape({
      nome: PropTypes.string.isRequired,
    }).isRequired,
    status: PropTypes.oneOf([
      'Pendente',
      'Em_Analise',
      'Aprovada',
      'Matriculado',
      'Rejeitada',
      'Desistente'
    ]).isRequired,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
};

export default PreInscricaoCard;